import { useEffect } from 'react';
import { useStore } from '../providers/StoreProvider';

declare global {
  interface Window {
    $crisp?: unknown[];
    CRISP_WEBSITE_ID?: string;
    CRISP_RUNTIME_CONFIG?: { locale?: string };
  }
}

/** Open the Crisp chat window (loads if needed). */
export function openCrispChat(): void {
  if (typeof window === 'undefined') return;
  const websiteId = (import.meta.env.VITE_CRISP_WEBSITE_ID || '').trim();
  if (!websiteId) {
    // Fallback: open Crisp embed in a new tab if widget id missing
    window.open('https://crisp.chat', '_blank', 'noopener,noreferrer');
    return;
  }
  window.$crisp = window.$crisp || [];
  window.CRISP_WEBSITE_ID = websiteId;
  if (!document.getElementById('freebi-crisp-chat')) {
    const s = document.createElement('script');
    s.id = 'freebi-crisp-chat';
    s.src = 'https://client.crisp.chat/l.js';
    s.async = true;
    document.head.appendChild(s);
  }
  try {
    window.$crisp.push(['do', 'chat:show']);
    window.$crisp.push(['do', 'chat:open']);
  } catch {
    /* queue until Crisp boots */
    window.$crisp.push(['do', 'chat:open']);
  }
}

/**
 * Crisp live chat for Freebi merchants (admin app only).
 * Website ID from VITE_CRISP_WEBSITE_ID (build-time env on Railway).
 * When store is loaded, session is tagged with shop domain for support context.
 */
export function CrispChat() {
  const { store } = useStore();
  const websiteId = (import.meta.env.VITE_CRISP_WEBSITE_ID || '').trim();

  // Load Crisp script once
  useEffect(() => {
    if (!websiteId) return;
    if (typeof window === 'undefined') return;

    window.$crisp = window.$crisp || [];
    window.CRISP_WEBSITE_ID = websiteId;

    if (document.getElementById('freebi-crisp-chat')) return;

    const s = document.createElement('script');
    s.id = 'freebi-crisp-chat';
    s.src = 'https://client.crisp.chat/l.js';
    s.async = true;
    document.head.appendChild(s);

    return () => {
      // Keep Crisp across SPA navigations — only remove on full unmount of app root
      // (this component stays mounted under App)
    };
  }, [websiteId]);

  // Identify merchant shop for support inbox
  useEffect(() => {
    if (!websiteId || !store?.shopDomain) return;
    if (typeof window === 'undefined') return;

    const crisp = window.$crisp;
    if (!Array.isArray(crisp)) return;

    try {
      crisp.push(['set', 'session:data', [
        ['shop_domain', store.shopDomain],
        ['store_name', store.name || store.shopDomain],
        ['currency', store.currency || ''],
        ['store_id', store.id || ''],
      ]]);
      crisp.push([
        'set',
        'session:segments',
        [['freebi-admin', store.shopDomain]],
      ]);
      // Nickname in Crisp inbox
      crisp.push([
        'set',
        'user:nickname',
        [store.name || store.shopDomain.replace('.myshopify.com', '')],
      ]);
      crisp.push(['set', 'user:company', [{ name: store.shopDomain }]]);
    } catch {
      /* Crisp not ready yet — data is re-set when store loads */
    }
  }, [
    websiteId,
    store?.shopDomain,
    store?.name,
    store?.currency,
    store?.id,
  ]);

  return null;
}
